import React, { Component, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import uniqBy from 'lodash/uniqBy';
import omitBy from 'lodash/omitBy';
import omit from 'lodash/omit';

import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { createResourceLocatorString } from '../../../util/routes';
import { getSearchPageResourceLocatorStringParams } from '../SearchPage.shared';
import { parse } from '../../../util/urlHelpers';

import { Button, InlineTextButton } from '../../../components';

import PopupOpenerButton from '../PopupOpenerButton/PopupOpenerButton';
import css from './SearchFiltersMobile.module.css';
import Drawer from 'react-modern-drawer';
import '../../../styles/drawer.css';
import Icons from '../../../components/Icons/Icons';

// --------------------
// Helpers (same idea as MainPanelHeader)
// --------------------
const isEmptyValue = v =>
  v == null ||
  v === '' ||
  (Array.isArray(v) && v.length === 0) ||
  (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);

const cleanSelectedFilters = selectedFilters => omitBy(selectedFilters || {}, isEmptyValue);

const toArrayValue = value => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    return value
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }
  return [value];
};

// pub_spirit_type -> spirit_type
const toConfigKey = urlKey => {
  if (!urlKey) return urlKey;
  return urlKey.startsWith('pub_') ? urlKey.replace(/^pub_/, '') : urlKey;
};

const getOptionLabel = (filterConfig, rawValue) => {
  const opts = filterConfig?.enumOptions || filterConfig?.options || [];
  const found = opts.find(o => `${o.option ?? o.key ?? o.id ?? o.value}` === `${rawValue}`);
  return found?.label ?? found?.optionLabel ?? found?.text ?? `${rawValue}`;
};

const getCategoryLabel = (listingCategories, rawValue) => {
  const categories = Array.isArray(listingCategories) ? listingCategories : [];
  const found = categories.find(c => `${c.id}` === `${rawValue}` || `${c.key}` === `${rawValue}`);
  return found?.name || found?.label || `${rawValue}`;
};

export const buildAppliedPills = ({ selectedFilters, availableFilters, listingCategories }) => {
  const cleaned = cleanSelectedFilters(selectedFilters);

  const IGNORE = new Set([
    'page',
    'mapSearch',
    'origin',
    'bounds',
    'address',
    'keywords',
    'sort',
  ]);

  const filtersByKey = new Map((availableFilters || []).map(f => [f.key, f]));
  const pills = [];

  Object.entries(cleaned).forEach(([urlKey, rawValue]) => {
    if (IGNORE.has(urlKey)) return;

    const configKey = toConfigKey(urlKey);
    const filterConfig = filtersByKey.get(configKey);

    const values = toArrayValue(rawValue);

    values.forEach(v => {
      const label =
        configKey === 'categoryLevel'
          ? getCategoryLabel(listingCategories, v)
          : filterConfig
          ? getOptionLabel(filterConfig, v)
          : `${v}`;

      pills.push({
        urlKey, // needed to remove from URL
        configKey,
        value: v,
        label,
      });
    });
  });

  return uniqBy(pills, p => `${p.urlKey}:${p.value}`);
};

class SearchFiltersMobileComponent extends Component {
  constructor(props) {
    super(props);
    this.state = { isFiltersOpenOnMobile: false, initialQueryParams: null };
    this.openFilters = this.openFilters.bind(this);
    this.cancelFilters = this.cancelFilters.bind(this);
    this.closeFilters = this.closeFilters.bind(this);
    this.resetAll = this.resetAll.bind(this);
  }

  openFilters() {
    const { onOpenModal, urlQueryParams } = this.props;
    onOpenModal();
    this.setState({ isFiltersOpenOnMobile: true, initialQueryParams: urlQueryParams });
  }

  cancelFilters() {
    const { history, onCloseModal, location, routeConfiguration } = this.props;

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(
        routeName,
        routeConfiguration,
        pathParams,
        this.state.initialQueryParams
      )
    );

    onCloseModal();
    this.setState({ isFiltersOpenOnMobile: false, initialQueryParams: null });
  }

  closeFilters() {
    this.props.onCloseModal();
    this.setState({ isFiltersOpenOnMobile: false });
  }

  resetAll(e) {
    this.props.resetAll(e);
    if (e && e.currentTarget) e.currentTarget.blur();
  }

  render() {
    const {
      rootClassName,
      className,
      children,
      sortByComponent,
      listingsAreLoaded,
      resultsCount,
      searchInProgress = false,
      onMapIconClick = () => {},
      selectedFiltersCount = 0,
      noResultsInfo,
      intl,
      isMapVariant = true,

      // dynamic pills + keyword search
      urlQueryParams,
      availableFilters,
      listingCategories,
      onClearAll,
      onRemoveFilter,

      // keyword search routing
      history,
      location,
      routeConfiguration,
      keywordValue,
      setKeywordValue,
    } = this.props;

    const classes = classNames(rootClassName || css.root, className);

    const resultsFound = (
      <FormattedMessage id="SearchFiltersMobile.foundResults" values={{ count: resultsCount }} />
    );
    const noResults = <FormattedMessage id="SearchFiltersMobile.noResults" />;
    const loadingResults = <FormattedMessage id="SearchFiltersMobile.loadingResults" />;
    const filtersHeading = intl.formatMessage({ id: 'SearchFiltersMobile.heading' });

    const showListingsLabel = intl.formatMessage(
      { id: 'SearchFiltersMobile.showListings' },
      { count: resultsCount }
    );

    const appliedPills = buildAppliedPills({
      selectedFilters: urlQueryParams,
      availableFilters,
      listingCategories,
    });

    return (
      <div className={classes}>
        <div className={css.searchResultSummary}>
          {listingsAreLoaded && resultsCount > 0 ? (
            <h2>
              <strong>{resultsFound}</strong>
            </h2>
          ) : null}
          {listingsAreLoaded && resultsCount === 0 ? noResults : null}
          {searchInProgress ? loadingResults : null}
        </div>

        <div className={css.buttons}>
          <PopupOpenerButton isSelected={selectedFiltersCount > 0} toggleOpen={this.openFilters}>
            <FormattedMessage id="SearchFiltersMobile.filtersButtonLabel" className={css.mapIconText} />
          </PopupOpenerButton>

          {sortByComponent}

          {isMapVariant ? (
            <div className={css.mapIcon} onClick={onMapIconClick}>
              <FormattedMessage id="SearchFiltersMobile.openMapView" className={css.mapIconText} />
            </div>
          ) : null}

          {/* ✅ Mobile keyword search works like MainPanelHeader */}
          <div className={css.searchContainer}>
            <Icons name="search" />
            <input
              value={keywordValue}
              onChange={e => setKeywordValue(e.target.value)}
              placeholder="Search by product name, producer or barrel ID..."
            />

            {/* {keywordValue ? (
              <button
                type="button"
                className={css.clearSearchBtn}
                onClick={() => setKeywordValue('')}
                aria-label="Clear search"
              >
                <Icons name="cross" />
              </button>
            ) : null} */}
          </div>
        </div>

        {/* ✅ Dynamic pills */}
        <div className={css.appliedFilters}>
          {appliedPills.map(pill => (
            <span key={`${pill.urlKey}-${pill.value}`} className={css.pill}>
              {pill.label}{' '}
              <span
                className={css.delete}
                role="button"
                tabIndex={0}
                onClick={() => onRemoveFilter?.(pill.urlKey, pill.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') onRemoveFilter?.(pill.urlKey, pill.value);
                }}
              >
                <Icons name="cross" />
              </span>
            </span>
          ))}

          {appliedPills.length > 0 ? (
            <InlineTextButton className={css.clear} onClick={onClearAll}>
              Clear
            </InlineTextButton>
          ) : null}
        </div>

        {noResultsInfo ? noResultsInfo : null}

        <Drawer
          open={this.state.isFiltersOpenOnMobile}
          onClose={this.cancelFilters}
          direction="bottom"
          className={css.filterDrawer}
          lockBackgroundScroll
          overlayOpacity="0.45"
          overlayColor="#000000"
          aria-label={intl.formatMessage({ id: 'SearchFiltersMobile.filters' })}
        >
          <div className={css.modalHeadingWrapper}>
            <span className={css.modalHeading}>{filtersHeading}</span>
            <button className={css.resetAllButton} onClick={e => this.resetAll(e)}>
              <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
            </button>
          </div>

          {this.state.isFiltersOpenOnMobile ? (
            <div className={css.filtersWrapper}>{children}</div>
          ) : null}

          <div className={css.showListingsContainer}>
            <Button className={css.showListingsButton} onClick={this.closeFilters}>
              {showListingsLabel}
            </Button>
          </div>
        </Drawer>
      </div>
    );
  }
}

/**
 * Wrapper that adds keyword URL-sync logic like MainPanelHeader
 */
const SearchFiltersMobile = props => {
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();

  // location is passed from SearchPage into SearchFiltersMobile already
  const location = props.location;

  // Read current keywords from URL
  const urlParams = useMemo(() => parse(location?.search || ''), [location?.search]);
  const currentKeywords = urlParams?.keywords || '';

  const [keywordValue, setKeywordValue] = useState(currentKeywords);

  // Keep input synced on back/forward etc
  useEffect(() => {
    setKeywordValue(currentKeywords);
  }, [currentKeywords]);

  // Debounce pushing to URL
  useEffect(() => {
    const t = setTimeout(() => {
      if (!history || !routeConfiguration || !location) return;

      // Only push if changed
      if ((currentKeywords || '') === (keywordValue || '')) return;

      const nextParams =
        keywordValue && keywordValue.trim().length > 0
          ? { ...urlParams, keywords: keywordValue.trim() }
          : omit(urlParams, 'keywords'); // ✅ removes keywords completely (no ?keywords=)

      const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
        routeConfiguration,
        location
      );

      history.push(createResourceLocatorString(routeName, routeConfiguration, pathParams, nextParams));
    }, 350);

    return () => clearTimeout(t);
  }, [keywordValue, history, routeConfiguration, location]); // urlParams/currentKeywords handled via memo deps above

  return (
    <SearchFiltersMobileComponent
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      keywordValue={keywordValue}
      setKeywordValue={setKeywordValue}
      {...props}
    />
  );
};

export default SearchFiltersMobile;
